// Landing pages P1b — starter presets for the Tools › Landing pages gallery
// (Screen A). Each preset is a plain block array using ONLY the block types the
// server-side LandingPageRenderer supports (eyebrow / heading / markdown /
// bullets / button / testimonial / image / divider / form) — so a card renders
// identically in the builder's live preview and on the published page.
//
// Picking a card POSTs a new draft with these `blocks` verbatim and opens it in
// the builder, so the copy already reads as a sensible starting point. Copy is
// GENERIC service-seller framing (accountants / cafés / studios / IT firms) —
// never our internal web-agency use case. Form blocks use form_id: 0 (renders
// nothing until the builder's Form destination picks a capture form).
//
// `kind` is the plain-English page description fed to the `landing.copy` AI
// prompt when the operator clicks "Draft copy from pipeline".

export default [
  {
    key: 'hero-form',
    name: 'Hero + capture form',
    description: 'Bold headline beside a short form. The all-rounder for a service offer.',
    kind: 'hero with a capture form',
    blocks: [
      { type: 'eyebrow', text: 'Free consultation' },
      { type: 'heading', level: 1, text: 'A better way to run the numbers for your business' },
      { type: 'markdown', body: 'Tell us where you want to get to and we’ll show you the shortest route — no jargon, no obligation.' },
      { type: 'bullets', items: [
        'A clear picture of where you stand today',
        'The three changes that move the needle first',
        'A plan you can actually action this quarter',
      ] },
      { type: 'form', form_id: 0 },
    ],
  },
  {
    key: 'long-form',
    name: 'Long-form + testimonial',
    description: 'Room to explain the offer, anchored by a client quote. Good for higher-value services.',
    kind: 'long-form sales page with a testimonial',
    blocks: [
      { type: 'eyebrow', text: 'For growing businesses' },
      { type: 'heading', level: 1, text: 'Expert help, without the big-firm price tag' },
      { type: 'markdown', body: 'You started your business to do the work you love — not to wrestle with spreadsheets, deadlines and admin. We take that off your plate so you can get back to it.' },
      { type: 'bullets', items: [
        'A dedicated point of contact who knows your business',
        'Plain-English advice whenever you need it',
        'Everything handled on time, every time',
      ] },
      { type: 'testimonial', quote: 'They spotted things our last provider never mentioned. It paid for itself in the first month.', author: 'A happy client' },
      { type: 'divider' },
      { type: 'heading', level: 2, text: 'See what we could do for you' },
      { type: 'markdown', body: 'Book a free, no-pressure call and we’ll walk you through exactly how it would work.' },
      { type: 'form', form_id: 0 },
    ],
  },
  {
    key: 'event',
    name: 'Event / webinar',
    description: 'Date, speakers and a register button. For seminars, clinics and Q&A sessions.',
    kind: 'event or webinar registration page',
    blocks: [
      { type: 'eyebrow', text: 'Free online session' },
      { type: 'heading', level: 1, text: 'Live workshop: the essentials, explained simply' },
      { type: 'markdown', body: 'Join us for a practical, 45-minute session — bring your questions and leave with a clear next step. Can’t make it live? Register anyway and we’ll send the recording.' },
      { type: 'bullets', items: [
        'What good looks like, with real examples',
        'The mistakes that cost the most — and how to avoid them',
        'Live Q&A with plenty of time for your questions',
      ] },
      { type: 'form', form_id: 0 },
    ],
  },
  {
    key: 'lead-magnet',
    name: 'Download / lead magnet',
    description: 'Trade a guide or checklist for an email. Fast to launch and easy to share.',
    kind: 'download / lead-magnet page offering a free guide',
    blocks: [
      { type: 'eyebrow', text: 'Free download' },
      { type: 'heading', level: 1, text: 'The plain-English checklist most businesses miss' },
      { type: 'markdown', body: 'A short, practical checklist you can work through in an afternoon — no fluff, just the things that actually matter.' },
      { type: 'bullets', items: [
        'The steps worth doing before anything else',
        'Where most people quietly lose money',
        'A simple readiness check for what’s next',
      ] },
      { type: 'form', form_id: 0 },
    ],
  },
  {
    key: 'pricing',
    name: 'Pricing teaser',
    description: 'Three packages side by side with an enquiry form. For productised services.',
    kind: 'pricing teaser page with packages and an enquiry form',
    blocks: [
      { type: 'heading', level: 1, text: 'Simple, honest pricing' },
      { type: 'markdown', body: 'Pick the level of support that fits where you are now — move up or down any time.' },
      { type: 'bullets', items: [
        'Starter — the essentials, handled for you',
        'Growth — everything in Starter, plus proactive advice',
        'Partner — a hands-on partner for your whole business',
      ] },
      { type: 'markdown', body: 'Not sure which is right? Tell us a little about your business and we’ll recommend one.' },
      { type: 'form', form_id: 0 },
    ],
  },
  {
    key: 'bare-form',
    name: 'Bare form',
    description: 'Just a heading and the fields. Fastest to publish; pairs well with an ad or QR code.',
    kind: 'minimal capture form page',
    blocks: [
      { type: 'heading', level: 1, text: 'Get in touch' },
      { type: 'markdown', body: 'Leave your details and we’ll be in touch shortly.' },
      { type: 'form', form_id: 0 },
    ],
  },
];
